<template>
  <FlotoFixedView>
    <FlotoPageHeader
      :title="`${resource.name}: ${resource.displayName}`"
      class="px-4"
      use-divider
    >
      <template v-slot:back-button>
        <slot name="before-title"></slot>
      </template>
      <template v-slot:title-append>
        <FlotoLink
          v-if="!disabled && purchaseStatus === statusName['generated']"
          :to="
            $modules.getModuleRoute('purchase', 'edit', {
              params: { id: resource.id },
            })
          "
          class="text-neutral md-text-base mx-2"
        >
          <MTooltip>
            <template v-slot:trigger>
              <MButton
                variant="neutral-light"
                :shadow="false"
                shape="circle"
                class="text-neutral md-text-base"
              >
                <MIcon name="pencil" />
              </MButton>
            </template>
            {{ $t('edit') }}
          </MTooltip>
        </FlotoLink>
      </template>
      <div class="flex justify-between">
        <small class="text-neutral">
          {{ $t('by') }}
          <FlotoUserDrawer :user-id="resource.requesterId" />
          {{ $t('at') }}
          {{ resource.createdAt | datetime }}
          ({{ resource.createdAt | timeago }})
        </small>
      </div>
      <slot name="additional-header-content" />
      <template v-slot:after-title>
        <slot name="actions" />
      </template>
    </FlotoPageHeader>
    <FlotoScrollView>
      <MCol :size="12" class="px-4">
        <MRow class="flex-no-wrap">
          <MCol
            class="transition-width main-detail-container"
            v-bind="detailSize"
          >
            <slot
              v-if="
                resource.description &&
                (!allowedFields || allowedFields.indexOf('description') >= 0)
              "
              name="description"
            >
              <div>
                <small class="text-neutral-light">
                  {{ $t('description') }}
                </small>
              </div>
              <div class="description my-2 mr-4">
                <FlotoReadMore :height="20 * 3">
                  <FlotoRichEditor :value="resource.description" disabled />
                </FlotoReadMore>
              </div>
            </slot>
            <slot name="primary-row">
              <PrimaryRow
                v-model="primaryInfo"
                :disabled="disabled"
                :module-name="moduleName"
              />
            </slot>
            <slot name="secondary-row">
              <SecondaryRow
                v-model="secondaryInfo"
                :disabled="disabled"
                :module-name="moduleName"
                :resource="resource"
                :purchase-status="purchaseStatus"
              />
            </slot>
            <slot name="tags-row">
              <TagsRow
                v-model="tagsInfo"
                :disabled="disabled"
                :module-name="moduleName"
                :resource="resource"
                :is-approval="isApproval"
                :approval="approval"
                @identifiedAsProblem="$emit('identifiedAsProblem')"
              />
            </slot>
            <slot v-if="allowAttachments" name="attachments">
              <FlotoAttachment
                :disabled="disabled"
                :value="resource.fileAttachments"
                only-list
                @change="updateResource({ fileAttachments: $event })"
              />
            </slot>
            <slot name="additional-fields">
              <AdditionalInfoWithExpand
                :resource="resource"
                :module-name="moduleName"
                :show-no-data="false"
                :disabled="disabled"
                :col-size="additionalInfoColSize"
                :update-fn="handleUpdateMoreDetails"
              />
            </slot>
            <slot />
          </MCol>
          <MCol
            v-if="hasSidebar"
            v-bind="sidebarSize"
            class="border border-neutral-lighter sidebar-border border-b-0 border-t-0 border-solid transition-width py-6 relative pages-inner-right-slide-panel"
            style="min-height: 120px"
          >
            <slot v-if="sidebarVisible" name="sidebar">
              <div class="overflow-x-hidden">
                <Sidebar
                  v-model="sidebarInfo"
                  :disabled="disabled"
                  :module-name="moduleName"
                  :purchase-status="purchaseStatus"
                />
              </div>
            </slot>
            <div class="toggle">
              <a
                href="javascript:;"
                class="text-neutral hover:text-neutral"
                @click="sidebarVisible = !sidebarVisible"
              >
                <MIcon :name="`chevron-${isRtl ? sidebarRtl : sidebarMain}`" />
              </a>
            </div>
          </MCol>
        </MRow>
      </MCol>
    </FlotoScrollView>
  </FlotoFixedView>
</template>

<script>
import IsEqual from 'lodash/isEqual'
import Pick from 'lodash/pick'
import { authComputed } from '@state/modules/auth'
import AdditionalInfoWithExpand from '@components/additional-information/additional-info-expandable'
import { OrganizationComputed } from '@state/modules/organization'
import PrimaryRow from './primary-row'
import SecondaryRow from './secondary-row'
import TagsRow from './tags-row'
import Sidebar from './sidebar'
import { getPurchaseStatusSystemName } from '../../helpers/status'

export default {
  name: 'PurchaseDetail',
  components: {
    PrimaryRow,
    SecondaryRow,
    TagsRow,
    Sidebar,
    AdditionalInfoWithExpand,
  },
  props: {
    disabled: { type: Boolean, default: false },
    resource: { type: Object, required: true },
    isApproval: { type: Boolean, default: false },
    approval: { type: Object, default: undefined },
    // eslint-disable-next-line
    allowAttachments: { type: Boolean, default: true },
    // eslint-disable-next-line
    hasSidebar: { type: Boolean, default: true },
    // eslint-disable-next-line
    showRequester: { type: Boolean, default: true },
    moduleName: { type: String, default: 'purchase' },
    updateApi: { type: Function, required: true },
    sidebarWidth: { type: Object, default: () => ({ size: 3 }) },
    detailWidth: { type: Object, default: () => ({ size: 9 }) },
    defaultSidebarVisible: { type: Boolean, default: false },
    allowedFields: { type: Array, default: undefined },
    titleLabel: {
      type: String,
      default: function () {
        return this.$t('name')
      },
    },
    descriptionLabel: {
      type: String,
      default: function () {
        return this.$t('description')
      },
    },
    isTicketClosed: { type: Boolean, default: false },
    additionalInfoColSize: { type: Number, default: undefined },
    purchaseStatus: { type: String, default: undefined },
  },
  data() {
    // @TODO make this as per preference
    this.statusName = getPurchaseStatusSystemName
    return {
      sidebarVisible: this.defaultSidebarVisible,
      updatingSubject: false,
      internalValueModel: {},
    }
  },
  computed: {
    ...authComputed,
    ...OrganizationComputed,
    sidebarRtl() {
      return this.sidebarVisible ? 'left' : 'right'
    },
    sidebarMain() {
      return this.sidebarVisible ? 'right' : 'left'
    },
    detailSize() {
      if (this.hasSidebar && this.sidebarVisible) {
        return this.detailWidth
      }
      return { size: 12 }
    },
    sidebarSize() {
      if (this.sidebarVisible) {
        return this.sidebarWidth
      }
      return { size: undefined }
    },
    primaryInfo: {
      get() {
        return Pick(this.resource, [
          'statusId',
          'costCenter',
          'vendorId',
          'technicianId',
        ])
      },
      set(value) {
        this.updateResource(value)
      },
    },
    secondaryInfo: {
      get() {
        return Pick(this.resource, [
          'orderNumber',
          'requiredByTime',
          'orderedDate',
          'totalCost',
          'currencyId',
        ])
      },
      set(value) {
        this.updateResource(value)
      },
    },
    tagsInfo: {
      get() {
        return Pick(this.resource, ['tags', 'approvalStatus'])
      },
      set(value) {
        this.updateResource(value)
      },
    },
    sidebarInfo: {
      get() {
        return Pick(this.resource, [
          'glCode',
          'invoiceReceived',
          'paymentStatus',
          'totalInvoiceAmount',
          'totalPaymentAmount',
          'updatedTime',
          'receivedDate',
          'purchaseCloseDate',
          'currencyId',
          'printTemplateId',
        ])
      },
      set(value) {
        this.updateResource(value)
      },
    },
  },
  methods: {
    handleUpdateMoreDetails(data) {
      const diff = Object.keys(data).filter(
        (k) => !IsEqual(this.resource.fieldValueDetails[k], data[k])
      )
      return this.updateResource({
        ...Pick(data, diff),
        fieldValueDetails: { ...this.resource.fieldValueDetails },
      })
    },
    updateResource(change) {
      return this.updateApi({ id: this.resource.id, ...change })
    },
    setInternalValue(change) {
      this.internalValueModel = {
        ...this.internalValueModel,
        ...(change || {}),
      }
    },
  },
}
</script>

<style lang="less" scoped>
.sub-heading {
  @apply flex items-center justify-between;
}

.heading {
  @apply items-start flex-wrap flex flex-col;

  .action {
    line-height: 30px;

    @apply ml-2 flex items-center justify-center absolute right-0 top-0;
  }

  .title {
    @apply overflow-hidden relative;
  }
}

.main-detail-container {
  &.@{ant-prefix}-col-12 {
    width: calc(100% - 34px);
  }
}
</style>
